import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, of, timer } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { Router, UrlTree } from "@angular/router";
import { StorageService } from "@services/storage.service";
import { environment } from "../../environments/environment";
import { LoginRequest } from "@models/interfaces/Auth/LoginRequest.interface";
import { AuthUser } from "@models/interfaces/Auth/AuthUser.interface";
import { SitesBuilderAuthSession } from "../auth-session";

enum Endpoints {
  LOGIN = '/auth/loginOL',
  REFRESH = '/auth/refresh-token'
};

const REFRESH_DELAY = 0;
const TOKEN_TTL = 9 * 60 * 1000;

@Injectable()
export class AuthService implements SitesBuilderAuthSession {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly storage: StorageService = inject(StorageService);
  private readonly router: Router = inject(Router);

  private _redirectUrl: string | null = null;

  public set redirectUrl(url: string) {
    this._redirectUrl = url;
  }

  public get redirectUrl(): string | null {
    return this._redirectUrl;
  }

  public clearRedirectUrl(): void {
    this._redirectUrl = null;
  }

  public login(user: Partial<LoginRequest>): Observable<AuthUser> {
    const body: LoginRequest = { ...user, appId: environment.appId } as LoginRequest;
    const headers = new HttpHeaders({ validation: 'onelogin' });
    return this.http.post<AuthUser>(environment.apiAuthUrl + Endpoints.LOGIN, body, { headers }).pipe(
      tap((res) => {
        this.storage.set('user', res);
        this.setupSilentRefresh();
      })
    );
  }

  public logout(navigate: boolean = true): void {
    this.storage.remove('user');
    if (navigate) this.router.navigate(['/auth/login']);
  }

  public isAuthenticated(): boolean {
    return this.isTokenValid();
  }

  public getAuthorizationHeader(): string | undefined {
    const user = this.storage.get<AuthUser>('user');
    if (!user || !this.isTokenValid() || !user.token_type || !user.access_token) {
      return undefined;
    }
    return `${user.token_type} ${user.access_token}`;
  }

  public redirectUnauthenticated(returnUrl: string): UrlTree {
    this.redirectUrl = returnUrl;
    this.logout(false);
    return this.router.createUrlTree(['/auth/login']);
  }

  public isTokenValid(): boolean {
    const user = this.storage.get<AuthUser>('user');
    return !!user && new Date() <= new Date(user.expires_in);
  }

  public setupSilentRefresh(): void {
    if (!this.isTokenValid()) return;

    timer(REFRESH_DELAY, TOKEN_TTL).pipe(
      switchMap(() => {
        const user = this.storage.get<AuthUser>('user');
        if (user && user.refresh_token) {
          return this.refreshToken();
        } else {
          this.logout();
          return of(null);
        }
      })
    ).subscribe();
  }

  private refreshToken(): Observable<AuthUser | null> {
    const user = this.storage.get<AuthUser>('user');
    if (!user || !user.refresh_token || !user.token_type) {
      this.logout();
      return of(null);
    }
    const { refresh_token, token_type } = user;
    const headers = new HttpHeaders({
      Authorization: `${token_type} ${refresh_token}`,
      validation: 'onelogin'
    });

    const body = { appId: environment.appId };

    return this.http.post<AuthUser>(environment.apiAuthUrl + Endpoints.REFRESH, body, { headers })
      .pipe(
        tap({
          next: (user) => this.storage.set('user', user),
          error: () => this.logout()
        })
      );
  }
}
